import { fmt, fmtRupiah, COMBORAN_INGREDIENTS, LOGO_URL, DAYS_PER_MONTH } from "@/lib/feedModels";

const formatDate = (iso) => {
  if (!iso) return new Date().toLocaleString("id-ID");
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function PrintReport({ calc }) {
  if (!calc) return null;

  const totalWeight = calc.totalWeight ?? calc.total_weight;
  const totalFeed = calc.totalFeed ?? calc.total_feed;
  const comboranKg = calc.comboranKg ?? calc.comboran_kg;
  const hijauanKg = calc.hijauanKg ?? calc.hijauan_kg;
  const isSilase = (calc.model?.type ?? calc.model_type) === "silase";
  const silaseKg = calc.silaseKg ?? calc.silase_kg ?? null;
  const effSilaseKg = silaseKg ?? (isSilase ? hijauanKg * 0.5 : 0);
  const forageKg = isSilase ? effSilaseKg : hijauanKg;
  const pagiKg = calc.hijauanPagiKg ?? calc.hijauan_pagi_kg ?? forageKg * 0.3;
  const soreKg = calc.hijauanSoreKg ?? calc.hijauan_sore_kg ?? forageKg * 0.7;
  const konvPct = isSilase && hijauanKg ? Math.round((effSilaseKg / hijauanKg) * 100) : 50;
  const isV2 = (calc.calc_version ?? (calc.model ? 2 : 1)) >= 2;
  const forageLabel = isSilase ? "Hijauan Segar" : isV2 ? "Onggok (Hijauan)" : "Hijauan";
  const forageShort = isSilase ? "Silase" : isV2 ? "Onggok" : "Hijauan";
  const name = calc.name ?? null;
  const modelLabel = calc.model?.label ?? calc.model_label ?? "-";
  const comboranPct = totalFeed ? Math.round((comboranKg / totalFeed) * 100) : 0;
  const hijauanPct = 100 - comboranPct;
  const ingredientsRaw =
    calc.ingredients ??
    COMBORAN_INGREDIENTS.map((ing) => ({
      ...ing,
      kg: calc[ing.id],
      pct: comboranKg ? calc[ing.id] / comboranKg : ing.pct,
    }));
  const ingredients = ingredientsRaw.filter((ing) => !(ing.id === "onggok" && isV2 && !isSilase));
  const ingredientsMonthly = ingredients.map((ing) => ({
    ...ing,
    monthlyKg: ing.monthlyKg ?? ing.kg * DAYS_PER_MONTH,
  }));

  const dailyCost = calc.cost ? calc.cost.dailyCost : calc.daily_cost ?? 0;
  const monthlyCost = calc.cost ? calc.cost.monthlyCost : calc.monthly_cost ?? 0;
  const showCost = (dailyCost || 0) > 0 || (monthlyCost || 0) > 0;

  return (
    <div id="print-area" data-testid="print-report" className="hidden bg-white text-[#4A2E1A] p-8">
      <div className="border-b-2 border-[#A06E4D] pb-4 mb-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo Sembada Satwa" className="w-16 h-16 object-contain" />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-[#8A5230]">Sembada Satwa</h1>
            <p className="text-sm text-[#A06E4D]">Laporan Rincian Pakan Sapi</p>
          </div>
        </div>
        <p className="text-xs text-[#A06E4D]">{formatDate(calc.created_at)}</p>
      </div>

      <table className="w-full text-sm mb-6">
        <tbody>
          {name && (
            <tr className="border-b border-[#E0A988]/50">
              <td className="py-2 font-semibold">Nama Sapi / Kandang</td>
              <td className="py-2 text-right font-bold text-[#8A5230]">{name}</td>
            </tr>
          )}
          <tr className="border-b border-[#E0A988]/50">
            <td className="py-2 font-semibold">Model Pakan</td>
            <td className="py-2 text-right">
              {modelLabel} (Comboran : {forageShort} = {comboranPct}% : {hijauanPct}%)
            </td>
          </tr>
          <tr className="border-b border-[#E0A988]/50">
            <td className="py-2 font-semibold">Total Bobot Sapi</td>
            <td className="py-2 text-right font-mono-num">{fmt(totalWeight)} kg</td>
          </tr>
          <tr className="border-b border-[#E0A988]/50">
            <td className="py-2 font-semibold">Kebutuhan Pakan Harian (10% bobot)</td>
            <td className="py-2 text-right font-mono-num font-bold">{fmt(totalFeed)} kg</td>
          </tr>
          <tr className="border-b border-[#E0A988]/50">
            <td className="py-2 font-semibold">Kebutuhan Comboran</td>
            <td className="py-2 text-right font-mono-num">{fmt(comboranKg)} kg/hari</td>
          </tr>
          {isSilase ? (
            <>
              <tr className="border-b border-[#E0A988]/50">
                <td className="py-2 font-semibold">Kebutuhan {forageLabel}</td>
                <td className="py-2 text-right font-mono-num">{fmt(hijauanKg)} kg/hari</td>
              </tr>
              <tr className="border-b border-[#E0A988]/50">
                <td className="py-2 font-semibold">Kebutuhan Silase (konversi {konvPct}%)</td>
                <td className="py-2 text-right font-mono-num font-bold">{fmt(effSilaseKg)} kg/hari</td>
              </tr>
            </>
          ) : (
            <tr className="border-b border-[#E0A988]/50">
              <td className="py-2 font-semibold">Kebutuhan {forageLabel}</td>
              <td className="py-2 text-right font-mono-num">{fmt(hijauanKg)} kg/hari</td>
            </tr>
          )}
          <tr className="border-b border-[#E0A988]/50">
            <td className="py-2 pl-6 text-[#7A4A28]">— Pagi Hari (30%)</td>
            <td className="py-2 text-right font-mono-num">{fmt(pagiKg)} kg</td>
          </tr>
          <tr className="border-b border-[#E0A988]/50">
            <td className="py-2 pl-6 text-[#7A4A28]">— Sore Hari (70%)</td>
            <td className="py-2 text-right font-mono-num">{fmt(soreKg)} kg</td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-base font-bold mb-2 text-[#8A5230]">Rincian Comboran</h2>
      <table className="w-full text-sm border border-[#E0A988]/70 mb-6">
        <thead>
          <tr className="bg-[#F7E5D2]">
            <th className="py-2 px-3 text-left font-semibold border-b border-[#E0A988]/70">Bahan</th>
            <th className="py-2 px-3 text-right font-semibold border-b border-[#E0A988]/70">Persentase</th>
            <th className="py-2 px-3 text-right font-semibold border-b border-[#E0A988]/70">Per Hari</th>
            <th className="py-2 px-3 text-right font-semibold border-b border-[#E0A988]/70">Per Bulan (30 hari)</th>
          </tr>
        </thead>
        <tbody>
          {ingredientsMonthly.map((ing) => (
            <tr key={ing.id} className="border-b border-[#E0A988]/40">
              <td className="py-2 px-3">{ing.label}</td>
              <td className="py-2 px-3 text-right font-mono-num">{Math.round(ing.pct * 100)}%</td>
              <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(ing.kg)} kg</td>
              <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(ing.monthlyKg)} kg</td>
            </tr>
          ))}
          <tr className="bg-[#FBF3E8]">
            <td className="py-2 px-3 font-bold">Total</td>
            <td className="py-2 px-3 text-right font-mono-num font-bold">100%</td>
            <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(comboranKg)} kg</td>
            <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(comboranKg * DAYS_PER_MONTH)} kg</td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-base font-bold mb-2 text-[#8A5230]">Estimasi Kebutuhan Bulanan (30 hari)</h2>
      <table className="w-full text-sm border border-[#E0A988]/70">
        <tbody>
          <tr className="border-b border-[#E0A988]/40">
            <td className="py-2 px-3 font-semibold">Total Pakan</td>
            <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(totalFeed * DAYS_PER_MONTH)} kg</td>
          </tr>
          <tr className="border-b border-[#E0A988]/40">
            <td className="py-2 px-3 font-semibold">Comboran</td>
            <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(comboranKg * DAYS_PER_MONTH)} kg</td>
          </tr>
          <tr>
            <td className="py-2 px-3 font-semibold">{forageLabel}</td>
            <td className="py-2 px-3 text-right font-mono-num font-bold">{fmt(hijauanKg * DAYS_PER_MONTH)} kg</td>
          </tr>
          {isSilase && (
            <tr>
              <td className="py-2 px-3 font-semibold border-t border-[#E0A988]/40">Silase</td>
              <td className="py-2 px-3 text-right font-mono-num font-bold border-t border-[#E0A988]/40">{fmt(effSilaseKg * DAYS_PER_MONTH)} kg</td>
            </tr>
          )}
        </tbody>
      </table>

      {isSilase && (
        <p data-testid="print-silase-note" className="text-xs text-[#7A4A28] mt-4">
          Catatan pembuatan silase: siapkan serbuk Polar kering 3-5% dari berat hijauan segar
          ({fmt(hijauanKg * 0.03)}-{fmt(hijauanKg * 0.05)} kg per hari) untuk dicampur saat inkubasi.
        </p>
      )}

      {showCost && (
        <>
          <h2 className="text-base font-bold mb-2 mt-6 text-[#8A5230]">Estimasi Biaya Pakan</h2>
          <table className="w-full text-sm border border-[#E0A988]/70">
            <tbody>
              <tr className="border-b border-[#E0A988]/40">
                <td className="py-2 px-3 font-semibold">Biaya per Hari</td>
                <td className="py-2 px-3 text-right font-mono-num font-bold">{fmtRupiah(dailyCost)}</td>
              </tr>
              <tr>
                <td className="py-2 px-3 font-semibold">Biaya per Bulan (30 hari)</td>
                <td className="py-2 px-3 text-right font-mono-num font-bold">{fmtRupiah(monthlyCost)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <p className="text-xs text-[#A06E4D] mt-8">
        Dicetak dari Sembada Satwa — Aplikasi Pembuat Pakan Sapi
      </p>
    </div>
  );
}
